import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences, type UserPreferencesPatch } from "@/hooks/useUserPreferences";
import StepWelcome from "./StepWelcome";
import StepCompany, { validateCompany } from "./StepCompany";
import StepTemplate, { validateTemplate } from "./StepTemplate";
import StepFirstSite, { validateSite } from "./StepFirstSite";
import StepRecap from "./StepRecap";
import type { CompanyValues, TemplateValues, SiteValues } from "./schemas";

const STEPS = ["Welcome", "Company", "Template", "First site", "Review"] as const;

interface SetupWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When true, the user has already finished/skipped — re-running manually. */
  manualMode?: boolean;
}

const EMPTY_COMPANY: CompanyValues = {
  displayName: "",
  company: "",
  peLicense: "",
  office: "",
};

const EMPTY_TEMPLATE: TemplateValues = {
  standard: "e1527-21",
  jurisdictionState: "",
  additionalRequirements: "",
};

const EMPTY_SITE: SiteValues = {
  address: "",
  city: "",
  state: "",
  zip: "",
  lat: null,
  lng: null,
};

export default function SetupWizard({
  open,
  onOpenChange,
  manualMode,
}: SetupWizardProps) {
  const { user } = useAuth();
  const { prefs, save } = useUserPreferences();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step state
  const [company, setCompany] = useState<CompanyValues>(EMPTY_COMPANY);
  const [template, setTemplate] = useState<TemplateValues>(EMPTY_TEMPLATE);
  const [site, setSite] = useState<SiteValues>(EMPTY_SITE);
  const [companyErrors, setCompanyErrors] = useState<Record<string, string>>({});
  const [templateErrors, setTemplateErrors] = useState<Record<string, string>>({});
  const [siteErrors, setSiteErrors] = useState<Record<string, string>>({});

  // Pre-fill from existing profile + prefs when opening
  useEffect(() => {
    if (!open || !user) return;
    setStep(0);
    setCompanyErrors({});
    setTemplateErrors({});
    setSiteErrors({});
    (async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name, company, pe_license")
        .eq("user_id", user.id)
        .maybeSingle();
      setCompany({
        displayName:
          profile?.display_name ||
          (user.user_metadata?.full_name as string | undefined) ||
          "",
        company: profile?.company ?? "",
        peLicense: profile?.pe_license ?? "",
        office: "",
      });
      setTemplate({
        standard: (prefs?.default_report_standard as TemplateValues["standard"]) || "e1527-21",
        jurisdictionState: prefs?.default_jurisdiction_state ?? "",
        additionalRequirements: prefs?.default_additional_requirements ?? "",
      });
      setSite({
        address: prefs?.default_site_address ?? "",
        city: prefs?.default_site_city ?? "",
        state: prefs?.default_site_state ?? "",
        zip: prefs?.default_site_zip ?? "",
        lat: prefs?.default_site_lat ?? null,
        lng: prefs?.default_site_lng ?? null,
      });
    })();
  }, [open, user, prefs]);

  const progress = useMemo(
    () => Math.round(((step + 1) / STEPS.length) * 100),
    [step],
  );

  const canAdvance = () => {
    if (step === 1) {
      const r = validateCompany(company);
      if (!r.ok) {
        setCompanyErrors(r.errors);
        return false;
      }
      setCompanyErrors({});
    }
    if (step === 2) {
      const r = validateTemplate(template);
      if (!r.ok) {
        setTemplateErrors(r.errors);
        return false;
      }
      setTemplateErrors({});
    }
    if (step === 3) {
      const r = validateSite(site);
      if (!r.ok) {
        setSiteErrors(r.errors);
        return false;
      }
      setSiteErrors({});
    }
    return true;
  };

  const next = () => {
    if (!canAdvance()) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleSkip = async () => {
    try {
      await save({ onboarding_skipped: true } as UserPreferencesPatch);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SetupWizard] skip persist failed", e);
    }
    toast("Setup skipped — you can run it again from Settings → Account.");
    onOpenChange(false);
  };

  const handleFinish = async () => {
    if (!user) return;
    if (!canAdvance()) return;
    setSubmitting(true);
    try {
      // 1. Profile (existing table — only update fields we actually have)
      await supabase
        .from("profiles")
        .upsert(
          [
            {
              user_id: user.id,
              email: user.email,
              display_name: company.displayName.trim(),
              company: (company.company ?? "").trim() || null,
              pe_license: (company.peLicense ?? "").trim() || null,
            },
          ],
          { onConflict: "user_id" },
        );

      // 2. Preferences
      await save({
        onboarding_completed_at: new Date().toISOString(),
        onboarding_skipped: false,
        default_report_standard: template.standard,
        default_jurisdiction_state:
          (template.jurisdictionState ?? "").trim() || null,
        default_additional_requirements:
          (template.additionalRequirements ?? "").trim() || null,
        default_site_address: (site.address ?? "").trim() || null,
        default_site_city: (site.city ?? "").trim() || null,
        default_site_state: (site.state ?? "").trim() || null,
        default_site_zip: (site.zip ?? "").trim() || null,
        default_site_lat: site.lat ?? null,
        default_site_lng: site.lng ?? null,
      } as UserPreferencesPatch);

      // 3. Optional draft report seeded from the first site
      const hasSite = !!(
        (site.address ?? "").trim() &&
        (site.city ?? "").trim() &&
        (site.state ?? "").trim()
      );
      if (hasSite) {
        const reportId = `R-${Date.now().toString(36).toUpperCase()}`;
        await supabase.from("reports").insert([
          {
            user_id: user.id,
            report_id: reportId,
            title: `Draft – ${site.address}`,
            type: "Phase 1 ESA",
            status: "draft",
            standard: template.standard,
            property_address: site.address,
            city: site.city,
            state: site.state,
            zip: site.zip || null,
            latitude: site.lat ?? null,
            longitude: site.lng ?? null,
            additional_requirements:
              (template.additionalRequirements ?? "").trim() || null,
          },
        ]);
      }

      toast.success(
        manualMode ? "Preferences updated" : "Setup complete — dashboard ready",
        {
          description: hasSite
            ? "Draft Phase I ESA report created from your first site."
            : "Defaults will pre-fill every new report.",
        },
      );
      onOpenChange(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SetupWizard] finish failed", e);
      toast.error("Couldn't save setup", {
        description:
          e instanceof Error ? e.message : "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isLast = step === STEPS.length - 1;

  return (
    <Dialog
      open={open}
      // Block accidental close so the user doesn't lose entered data;
      // they must click Skip or Finish explicitly.
      onOpenChange={(o) => {
        if (!o && submitting) return;
        if (!o && !manualMode) return; // require explicit choice
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="max-w-2xl"
        onEscapeKeyDown={(e) => {
          if (!manualMode) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (!manualMode) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {manualMode ? "Update setup" : "Welcome to EnviroEng Pro"}
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEPS.length} · {STEPS[step]}
          </DialogDescription>
        </DialogHeader>

        <Progress value={progress} className="h-1" />

        <div className="py-2 min-h-[320px]">
          {step === 0 && <StepWelcome displayName={company.displayName} />}
          {step === 1 && (
            <StepCompany
              values={company}
              onChange={setCompany}
              errors={companyErrors}
            />
          )}
          {step === 2 && (
            <StepTemplate
              values={template}
              onChange={setTemplate}
              errors={templateErrors}
            />
          )}
          {step === 3 && (
            <StepFirstSite
              values={site}
              onChange={setSite}
              errors={siteErrors}
            />
          )}
          {step === 4 && (
            <StepRecap company={company} template={template} site={site} />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <div className="flex items-center justify-between w-full gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              disabled={submitting}
            >
              {manualMode ? "Cancel" : "Skip for now"}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={back}
                disabled={step === 0 || submitting}
              >
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
              </Button>
              {!isLast ? (
                <Button type="button" size="sm" onClick={next}>
                  Continue <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleFinish}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Finish
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
